/* EventCard — Event listing card */

interface EventCardProps {
  data: Record<string, string>
  entityData?: Record<string, unknown>
}

export default function EventCard({ data, entityData }: EventCardProps) {
  const title = (entityData?.title as string) || data.title || 'Upcoming Event'
  const date = (entityData?.event_date as string) || data.date || ''
  const time = (entityData?.event_time as string) || data.time || ''
  const description = (entityData?.description as string) || data.description || ''
  const location = (entityData?.location as string) || data.location || ''

  // Format date for display
  const formattedDate = date ? new Date(date).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  }) : ''

  return (
    <div data-testid="card-event" className="group relative h-full rounded-xl overflow-hidden bg-gradient-to-br from-white/[0.04] to-white/[0.01] border border-white/[0.06] hover:border-white/[0.12] transition-[border-color,transform] duration-300 hover:-translate-y-0.5">
      <div className="p-5">
        {/* Date pill */}
        {formattedDate && (
          <div className="mb-3">
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-blue-500/10 text-blue-400/70 text-[10px] font-bold uppercase tracking-wider">
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              {formattedDate}
              {time && <span className="text-blue-400/40">· {time}</span>}
            </span>
          </div>
        )}

        {/* Title */}
        <h4 className="text-sm font-semibold text-white/90 mb-2 tracking-tight group-hover:text-white transition-colors">
          {title}
        </h4>

        {/* Location */}
        {location && (
          <div className="flex items-center gap-1 mb-2">
            <svg className="w-3 h-3 text-white/20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            <span className="text-[11px] text-white/25">{location}</span>
          </div>
        )}

        {/* Description */}
        {description && (
          <p className="text-xs text-white/30 leading-relaxed line-clamp-2 group-hover:text-white/40 transition-colors">
            {description}
          </p>
        )}
      </div>
    </div>
  )
}
